import z from 'zod';
export const themeColorPaletteSlotValue = z.enum([
    'accent1',
    'accent2',
    'accent3',
    'accent4',
    'accent5',
    'accent6',
    'dk1',
    'dk2',
    'folHlink',
    'hlink',
    'lt1',
    'lt2',
]);
export const themeColorPaletteSchema = z.object({
    accent1: z.string(),
    accent2: z.string(),
    accent3: z.string(),
    accent4: z.string(),
    accent5: z.string(),
    accent6: z.string(),
    dk1: z.string(),
    dk2: z.string(),
    folHlink: z.string(),
    hlink: z.string(),
    lt1: z.string(),
    lt2: z.string(),
});
export const wordTextColorAttributesSchema = z.object({
    themeColor: z.string().nullable().optional(),
    themeShade: z.string().nullable().optional(),
    themeTint: z.string().nullable().optional(),
    val: z.string().nullable().optional(),
});
const HEX_COLOR_PATTERN = /^[\dA-Fa-f]{6}$/;
const WORD_THEME_COLOR_TO_PALETTE_SLOT = {
    accent1: 'accent1',
    accent2: 'accent2',
    accent3: 'accent3',
    accent4: 'accent4',
    accent5: 'accent5',
    accent6: 'accent6',
    background1: 'lt1',
    background2: 'lt2',
    dark1: 'dk1',
    dark2: 'dk2',
    followedhyperlink: 'folHlink',
    hyperlink: 'hlink',
    light1: 'lt1',
    light2: 'lt2',
    text1: 'dk1',
    text2: 'dk2',
};
const parseHexRgb = (hexColor) => {
    const normalized = normalizeWordTextColorHex(hexColor);
    if (!normalized) {
        return null;
    }
    const rgb = normalized.slice(1);
    return [
        Number.parseInt(rgb.slice(0, 2), 16),
        Number.parseInt(rgb.slice(2, 4), 16),
        Number.parseInt(rgb.slice(4, 6), 16),
    ];
};
const parseUCharHex = (value) => {
    const trimmed = value.trim();
    if (!/^[\dA-Fa-f]{1,2}$/.test(trimmed)) {
        return null;
    }
    return Number.parseInt(trimmed, 16);
};
const rgbToHsl = (red, green, blue) => {
    const redUnit = red / 255;
    const greenUnit = green / 255;
    const blueUnit = blue / 255;
    const max = Math.max(redUnit, greenUnit, blueUnit);
    const min = Math.min(redUnit, greenUnit, blueUnit);
    let hue = 0;
    let saturation = 0;
    const lightness = (max + min) / 2;
    if (max !== min) {
        const delta = max - min;
        saturation =
            lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
        switch (max) {
            case blueUnit: {
                hue = ((redUnit - greenUnit) / delta + 4) / 6;
                break;
            }
            case greenUnit: {
                hue = ((blueUnit - redUnit) / delta + 2) / 6;
                break;
            }
            case redUnit: {
                hue = ((greenUnit - blueUnit) / delta + (greenUnit < blueUnit ? 6 : 0))
                    / 6;
                break;
            }
        }
    }
    return [hue, saturation, lightness];
};
const hslToRgb = (hue, saturation, lightness) => {
    if (saturation === 0) {
        const gray = Math.round(lightness * 255);
        return [gray, gray, gray];
    }
    const hueToChannel = (channelOffset) => {
        let channel = hue + channelOffset;
        if (channel < 0) {
            channel += 1;
        }
        if (channel > 1) {
            channel -= 1;
        }
        if (channel < 1 / 6) {
            return third + (quarter - third) * 6 * channel;
        }
        if (channel < 1 / 2) {
            return quarter;
        }
        if (channel < 2 / 3) {
            return third + (quarter - third) * (2 / 3 - channel) * 6;
        }
        return third;
    };
    const quarter = lightness < 0.5
        ? lightness * (1 + saturation)
        : lightness + saturation - lightness * saturation;
    const third = 2 * lightness - quarter;
    return [
        Math.round(hueToChannel(1 / 3) * 255),
        Math.round(hueToChannel(0) * 255),
        Math.round(hueToChannel(-1 / 3) * 255),
    ];
};
const channelToHex = (channel) => Math.max(0, Math.min(255, channel)).toString(16).padStart(2, '0');
const formatHexRgb = (red, green, blue) => `#${channelToHex(red)}${channelToHex(green)}${channelToHex(blue)}`;
const applyThemeModifiers = (baseColor, themeTint, themeShade) => {
    if (themeTint) {
        return applyWordThemeTint(baseColor, themeTint);
    }
    if (themeShade) {
        return applyWordThemeShade(baseColor, themeShade);
    }
    return baseColor;
};
export const normalizeWordTextColorHex = (value) => {
    const trimmed = value.trim().replace(/^#/, '');
    if (!HEX_COLOR_PATTERN.test(trimmed)) {
        return null;
    }
    return `#${trimmed.toLowerCase()}`;
};
export const mapWordThemeColorToPaletteSlot = (themeColor) => {
    const normalized = themeColor.trim().toLowerCase();
    if (normalized === 'none') {
        return null;
    }
    return WORD_THEME_COLOR_TO_PALETTE_SLOT[normalized] ?? null;
};
export const applyWordThemeTint = (baseColor, themeTint) => {
    const rgb = parseHexRgb(baseColor);
    const tintValue = parseUCharHex(themeTint);
    if (!rgb || tintValue === null) {
        return normalizeWordTextColorHex(baseColor) ?? baseColor;
    }
    const [hue, saturation, lightness] = rgbToHsl(...rgb);
    const tintPercentage = tintValue / 255;
    const adjustedLightness = lightness * tintPercentage + (1 - tintPercentage);
    const [red, green, blue] = hslToRgb(hue, saturation, adjustedLightness);
    return formatHexRgb(red, green, blue);
};
export const applyWordThemeShade = (baseColor, themeShade) => {
    const rgb = parseHexRgb(baseColor);
    const shadeValue = parseUCharHex(themeShade);
    if (!rgb || shadeValue === null) {
        return normalizeWordTextColorHex(baseColor) ?? baseColor;
    }
    const [hue, saturation, lightness] = rgbToHsl(...rgb);
    const shadePercentage = shadeValue / 255;
    const adjustedLightness = lightness * shadePercentage;
    const [red, green, blue] = hslToRgb(hue, saturation, adjustedLightness);
    return formatHexRgb(red, green, blue);
};
export const resolveWordTextColor = (attributes, palette) => {
    const themeColor = attributes.themeColor?.trim().toLowerCase();
    const themeTint = attributes.themeTint?.trim();
    const themeShade = attributes.themeShade?.trim();
    const value = attributes.val?.trim();
    if (themeColor && themeColor !== 'none') {
        const paletteSlot = mapWordThemeColorToPaletteSlot(themeColor);
        const paletteColor = paletteSlot && palette
            ? normalizeWordTextColorHex(palette[paletteSlot])
            : null;
        if (paletteColor) {
            return applyThemeModifiers(paletteColor, themeTint, themeShade);
        }
    }
    if (value?.toLowerCase() === 'auto') {
        return '#000000';
    }
    if (value) {
        return normalizeWordTextColorHex(value);
    }
    return null;
};
//# sourceMappingURL=word-text-color.js.map